import React, { useState, useMemo } from 'react';
import {
  PackageCheck,
  Package,
  Boxes,
  Tag,
  Barcode,
  Search,
  Filter,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FileText,
  Layers,
  Wrench,
  ExternalLink,
  ChevronRight,
  Sparkles,
  ArrowDownRight,
  ArrowUpRight,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { CompanyInfo, CurrencyCode, InventoryItem, ServiceTicket, StockMovement } from '../types';
import { formatTL, formatUSD, formatPhoneDisplay, getStoredExchangeRate, getTicketReportDate } from '../utils/storage';

interface StockUsageReportSectionProps {
  tickets: ServiceTicket[];
  inventory: InventoryItem[];
  stockMovements: StockMovement[];
  companyInfo: CompanyInfo;
  selectedPeriod: string; // 'all' or 'YYYY-MM'
  onPeriodChange: (p: string) => void;
  availableMonths: string[];
  onExportPDF: () => void;
  onExportExcel: () => void;
}

const MONTH_NAMES_TR: Record<string, string> = {
  '01': 'Ocak',
  '02': 'Şubat',
  '03': 'Mart',
  '04': 'Nisan',
  '05': 'Mayıs',
  '06': 'Haziran',
  '07': 'Temmuz',
  '08': 'Ağustos',
  '09': 'Eylül',
  '10': 'Ekim',
  '11': 'Kasım',
  '12': 'Aralık',
};

const CHART_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#64748b', '#f97316'];

export interface StockDeductionItem {
  id: string; // ticketId-partIdx
  date: string;
  ticketId: string;
  ticketStatus: string;
  customerName: string;
  customerPhone: string;
  device: string;
  partName: string;
  partCode: string;
  category: string;
  quantity: number;
  unitCost: number; // TL
  unitPrice: number; // TL
  totalCost: number; // TL
  totalSale: number; // TL
  netProfit: number; // TL
  marginPercent: number;
  inventoryItemId?: string;
  inventoryItem?: InventoryItem;
  currentStock: number | string;
  isStocked: boolean;
  isCritical: boolean;
  // Currency & Rate Tracking
  currency?: CurrencyCode;
  costPriceUSD?: number;
  salePriceUSD?: number;
  costExchangeRate?: number; // Alış esnasındaki Dolar Kuru (USD/TL)
  saleExchangeRate?: number; // Satış esnasındaki Dolar Kuru (USD/TL)
  rateDifference?: number; // Satış Kuru - Alış Kuru
  fxGainTL?: number; // Kur farkından doğan TL kâr / etki
  isUSD: boolean;
}

export interface ConsolidatedStockPart {
  key: string;
  partName: string;
  partCode: string;
  category: string;
  totalQuantity: number;
  totalCost: number;
  totalSale: number;
  netProfit: number;
  marginPercent: number;
  timesUsed: number;
  currentStock: number | string;
  inventoryItem?: InventoryItem;
  isCritical: boolean;
  isStocked: boolean;
  // USD / FX Details
  isUSD: boolean;
  totalCostUSD?: number;
  totalSaleUSD?: number;
  avgCostRate?: number;
  avgSaleRate?: number;
  rateDifference?: number;
  totalFxGainTL?: number;
}

export const StockUsageReportSection: React.FC<StockUsageReportSectionProps> = ({
  tickets,
  inventory,
  stockMovements,
  companyInfo,
  selectedPeriod,
  onPeriodChange,
  availableMonths,
  onExportPDF,
  onExportExcel,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'stocked' | 'external' | 'critical'>('all');
  const [currencyFilter, setCurrencyFilter] = useState<'all' | 'USD' | 'TRY'>('all');
  const [viewTab, setViewTab] = useState<'detailed' | 'consolidated'>('detailed');

  // Formatted period label
  const formattedPeriodLabel = useMemo(() => {
    if (selectedPeriod === 'all') return 'Tüm Zamanlar';
    try {
      const [year, month] = selectedPeriod.split('-');
      return `${MONTH_NAMES_TR[month] || month} ${year}`;
    } catch {
      return selectedPeriod;
    }
  }, [selectedPeriod]);

  // Filter tickets by selected period
  const periodTickets = useMemo(() => {
    if (selectedPeriod === 'all') return tickets;
    return tickets.filter(t => Boolean(getTicketReportDate(t) && getTicketReportDate(t).startsWith(selectedPeriod)));
  }, [tickets, selectedPeriod]);

  // Extract all individual part usages from filtered tickets with historical FX tracking
  const allDeductions = useMemo<StockDeductionItem[]>(() => {
    const list: StockDeductionItem[] = [];
    const liveRate = getStoredExchangeRate().rate || 47.95;

    periodTickets.forEach(t => {
      const tDate = getTicketReportDate(t).substring(0, 10);
      const devStr = [t.deviceType, t.deviceBrand, t.deviceModel].filter(Boolean).join(' ');

      (t.parts || []).forEach((p, idx) => {
        const qty = p.quantity || 1;

        // Find inventory item
        const invItem = p.inventoryItemId
          ? inventory.find(i => i.id === p.inventoryItemId)
          : inventory.find(i => i.name.trim().toLowerCase() === p.name.trim().toLowerCase());

        const isUSD =
          p.currency === 'USD' ||
          invItem?.currency === 'USD' ||
          (p.costPriceUSD !== undefined && p.costPriceUSD > 0) ||
          (invItem?.costPriceUSD !== undefined && (invItem.costPriceUSD || 0) > 0);

        const costUSD =
          p.costPriceUSD !== undefined && p.costPriceUSD > 0
            ? p.costPriceUSD
            : invItem?.costPriceUSD !== undefined && (invItem.costPriceUSD || 0) > 0
            ? invItem.costPriceUSD
            : undefined;

        const saleUSD =
          p.unitPriceUSD !== undefined && p.unitPriceUSD > 0
            ? p.unitPriceUSD
            : invItem?.salePriceUSD !== undefined && (invItem.salePriceUSD || 0) > 0
            ? invItem.salePriceUSD
            : undefined;

        // Historical cost exchange rate (at time of purchasing/stocking)
        const costExchangeRate =
          p.costExchangeRate ||
          invItem?.costExchangeRate ||
          invItem?.exchangeRate ||
          p.exchangeRate ||
          (isUSD ? liveRate : undefined);

        // Historical or transaction sale exchange rate (at time of selling/servicing)
        const saleExchangeRate =
          p.saleExchangeRate ||
          p.exchangeRate ||
          (isUSD ? liveRate : undefined);

        // Calculate unit cost in TL
        let uCost = p.costPrice !== undefined ? p.costPrice : 0;
        if (isUSD && costUSD && costExchangeRate && uCost <= 0) {
          uCost = Math.round(costUSD * costExchangeRate * 100) / 100;
        } else if (uCost <= 0 && invItem?.costPrice) {
          uCost = invItem.costPrice;
        }

        // Calculate unit price in TL
        let uPrice = p.unitPrice || 0;
        if (isUSD && saleUSD && saleExchangeRate && uPrice <= 0) {
          uPrice = Math.round(saleUSD * saleExchangeRate * 100) / 100;
        } else if (uPrice <= 0 && invItem?.salePrice) {
          uPrice = invItem.salePrice;
        }

        const totalCost = qty * uCost;
        const totalSale = qty * uPrice;
        const netProfit = totalSale - totalCost;
        const marginPercent = totalCost > 0 ? Math.round((netProfit / totalCost) * 100) : (totalSale > 0 ? 100 : 0);

        // Exchange rate difference and FX gain calculation
        const rateDifference =
          isUSD && costExchangeRate && saleExchangeRate
            ? Math.round((saleExchangeRate - costExchangeRate) * 10000) / 10000
            : undefined;

        const fxGainTL =
          isUSD && costUSD && costExchangeRate && saleExchangeRate
            ? Math.round((saleExchangeRate - costExchangeRate) * costUSD * qty * 100) / 100
            : 0;

        const partCode = invItem?.code || invItem?.id || '-';
        const category = invItem?.category || 'Genel';
        const currentStock = invItem ? (invItem.quantity ?? 0) : 'Harici';
        const isCritical = invItem ? (invItem.quantity <= (invItem.minQuantity || 2)) : false;

        list.push({
          id: `${t.id}-${idx}`,
          date: tDate,
          ticketId: t.id,
          ticketStatus: t.status,
          customerName: t.customerName || '',
          customerPhone: t.customerPhone || '',
          device: devStr,
          partName: p.name,
          partCode,
          category,
          quantity: qty,
          unitCost: uCost,
          unitPrice: uPrice,
          totalCost,
          totalSale,
          netProfit,
          marginPercent,
          inventoryItemId: p.inventoryItemId || invItem?.id,
          inventoryItem: invItem,
          currentStock,
          isStocked: !!invItem,
          isCritical,
          currency: isUSD ? 'USD' : 'TRY',
          costPriceUSD: costUSD,
          salePriceUSD: saleUSD,
          costExchangeRate,
          saleExchangeRate,
          rateDifference,
          fxGainTL,
          isUSD,
        });
      });
    });

    return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [periodTickets, inventory]);

  // Extract unique categories for filter dropdown
  const uniqueCategories = useMemo(() => {
    const set = new Set<string>();
    allDeductions.forEach(d => {
      if (d.category) set.add(d.category);
    });
    return Array.from(set).sort();
  }, [allDeductions]);

  // Filtered deductions based on search, categories, and currency filters
  const filteredDeductions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return allDeductions.filter(item => {
      // Category filter
      if (categoryFilter !== 'all' && item.category !== categoryFilter) {
        return false;
      }

      // Status filter
      if (statusFilter === 'stocked' && !item.isStocked) return false;
      if (statusFilter === 'external' && item.isStocked) return false;
      if (statusFilter === 'critical' && !item.isCritical) return false;

      // Currency filter
      if (currencyFilter === 'USD' && !item.isUSD) return false;
      if (currencyFilter === 'TRY' && item.isUSD) return false;

      // Search query
      if (query) {
        const matchesName = item.partName.toLowerCase().includes(query);
        const matchesCode = item.partCode.toLowerCase().includes(query);
        const matchesCustomer = item.customerName.toLowerCase().includes(query);
        const matchesTicket = item.ticketId.toLowerCase().includes(query);
        const matchesDevice = item.device.toLowerCase().includes(query);
        const matchesCat = item.category.toLowerCase().includes(query);
        if (!matchesName && !matchesCode && !matchesCustomer && !matchesTicket && !matchesDevice && !matchesCat) {
          return false;
        }
      }

      return true;
    });
  }, [allDeductions, searchQuery, categoryFilter, statusFilter, currencyFilter]);

  // Consolidated aggregation per part with USD & rate difference tracking
  const consolidatedParts = useMemo<ConsolidatedStockPart[]>(() => {
    const map = new Map<
      string,
      ConsolidatedStockPart & {
        costRateWeightedSum: number;
        costUSDQtySum: number;
        saleRateWeightedSum: number;
        saleUSDQtySum: number;
      }
    >();

    filteredDeductions.forEach(d => {
      const key = d.inventoryItemId || d.partName.trim().toLowerCase();
      const existing = map.get(key) || {
        key,
        partName: d.partName,
        partCode: d.partCode,
        category: d.category,
        totalQuantity: 0,
        totalCost: 0,
        totalSale: 0,
        netProfit: 0,
        marginPercent: 0,
        timesUsed: 0,
        currentStock: d.currentStock,
        inventoryItem: d.inventoryItem,
        isCritical: d.isCritical,
        isStocked: d.isStocked,
        isUSD: d.isUSD,
        totalCostUSD: 0,
        totalSaleUSD: 0,
        totalFxGainTL: 0,
        costRateWeightedSum: 0,
        costUSDQtySum: 0,
        saleRateWeightedSum: 0,
        saleUSDQtySum: 0,
      };

      existing.totalQuantity += d.quantity;
      existing.totalCost += d.totalCost;
      existing.totalSale += d.totalSale;
      existing.netProfit += d.netProfit;
      existing.timesUsed += 1;

      if (d.isUSD) {
        existing.isUSD = true;
        if (d.costPriceUSD) {
          existing.totalCostUSD = (existing.totalCostUSD || 0) + d.costPriceUSD * d.quantity;
          if (d.costExchangeRate) {
            existing.costRateWeightedSum += d.costExchangeRate * d.quantity;
            existing.costUSDQtySum += d.quantity;
          }
        }
        if (d.salePriceUSD) {
          existing.totalSaleUSD = (existing.totalSaleUSD || 0) + d.salePriceUSD * d.quantity;
          if (d.saleExchangeRate) {
            existing.saleRateWeightedSum += d.saleExchangeRate * d.quantity;
            existing.saleUSDQtySum += d.quantity;
          }
        }
        if (d.fxGainTL) {
          existing.totalFxGainTL = (existing.totalFxGainTL || 0) + d.fxGainTL;
        }
      }

      map.set(key, existing);
    });

    return Array.from(map.values())
      .map(p => {
        const margin = p.totalCost > 0 ? Math.round((p.netProfit / p.totalCost) * 100) : (p.totalSale > 0 ? 100 : 0);
        const avgCostRate = p.costUSDQtySum > 0 ? Math.round((p.costRateWeightedSum / p.costUSDQtySum) * 100) / 100 : undefined;
        const avgSaleRate = p.saleUSDQtySum > 0 ? Math.round((p.saleRateWeightedSum / p.saleUSDQtySum) * 100) / 100 : undefined;
        const rateDiff = avgCostRate && avgSaleRate ? Math.round((avgSaleRate - avgCostRate) * 100) / 100 : undefined;

        return {
          ...p,
          marginPercent: margin,
          avgCostRate,
          avgSaleRate,
          rateDifference: rateDiff,
        };
      })
      .sort((a, b) => b.totalQuantity - a.totalQuantity);
  }, [filteredDeductions]);

  // Summary KPI Metrics & Currency Rate Intelligence
  const metrics = useMemo(() => {
    let totalQty = 0;
    let totalCost = 0;
    let totalSale = 0;
    let totalCostUSD = 0;
    let totalSaleUSD = 0;
    let totalFxGainTL = 0;
    let usdItemsCount = 0;
    let weightedCostRateSum = 0;
    let weightedCostQty = 0;
    let weightedSaleRateSum = 0;
    let weightedSaleQty = 0;

    const distinctSet = new Set<string>();
    const ticketSet = new Set<string>();

    allDeductions.forEach(d => {
      totalQty += d.quantity;
      totalCost += d.totalCost;
      totalSale += d.totalSale;
      distinctSet.add(d.inventoryItemId || d.partName.trim().toLowerCase());
      ticketSet.add(d.ticketId);

      if (d.isUSD) {
        usdItemsCount += d.quantity;
        if (d.costPriceUSD) totalCostUSD += d.costPriceUSD * d.quantity;
        if (d.salePriceUSD) totalSaleUSD += d.salePriceUSD * d.quantity;
        if (d.fxGainTL) totalFxGainTL += d.fxGainTL;

        if (d.costExchangeRate) {
          weightedCostRateSum += d.costExchangeRate * d.quantity;
          weightedCostQty += d.quantity;
        }
        if (d.saleExchangeRate) {
          weightedSaleRateSum += d.saleExchangeRate * d.quantity;
          weightedSaleQty += d.quantity;
        }
      }
    });

    const netProfit = totalSale - totalCost;
    const marginPercent = totalCost > 0 ? Math.round((netProfit / totalCost) * 100) : 0;
    const avgCostRate = weightedCostQty > 0 ? Math.round((weightedCostRateSum / weightedCostQty) * 100) / 100 : undefined;
    const avgSaleRate = weightedSaleQty > 0 ? Math.round((weightedSaleRateSum / weightedSaleQty) * 100) / 100 : undefined;
    const rateDiff = avgCostRate && avgSaleRate ? Math.round((avgSaleRate - avgCostRate) * 100) / 100 : undefined;
    const currentRate = getStoredExchangeRate().rate || 47.95;

    return {
      totalQty,
      totalCost,
      totalSale,
      netProfit,
      marginPercent,
      distinctCount: distinctSet.size,
      ticketCount: ticketSet.size,
      usdItemsCount,
      totalCostUSD,
      totalSaleUSD,
      totalFxGainTL,
      avgCostRate,
      avgSaleRate,
      rateDiff,
      currentRate,
    };
  }, [allDeductions]);

  // Chart 1: Top 8 Used Parts (By Quantity & Revenue)
  const topUsedPartsChartData = useMemo(() => {
    return consolidatedParts.slice(0, 8).map(p => ({
      name: p.partName.length > 18 ? `${p.partName.substring(0, 16)}...` : p.partName,
      fullName: p.partName,
      quantity: p.totalQuantity,
      sale: p.totalSale,
      cost: p.totalCost,
      profit: p.netProfit,
    }));
  }, [consolidatedParts]);

  // Chart 2: Category Distribution
  const categoryPieData = useMemo(() => {
    const map = new Map<string, { qty: number; sale: number }>();
    allDeductions.forEach(d => {
      const cat = d.category || 'Genel';
      const cur = map.get(cat) || { qty: 0, sale: 0 };
      cur.qty += d.quantity;
      cur.sale += d.totalSale;
      map.set(cat, cur);
    });

    return Array.from(map.entries()).map(([name, val]) => ({
      name,
      value: val.qty,
      sale: val.sale,
    })).sort((a, b) => b.value - a.value);
  }, [allDeductions]);

  return (
    <div className="space-y-6">
      {/* 4 Prominent Stock KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Toplam Düşülen Parça */}
        <div className="bg-white rounded-2xl p-4.5 border border-slate-200/80 shadow-sm space-y-1.5">
          <div className="text-slate-500 text-xs font-semibold">
            <span>Toplam Düşülen Parça Adedi</span>
          </div>
          <div className="flex items-baseline space-x-2">
            <h3 className="text-2xl font-black text-slate-900">{metrics.totalQty} Adet</h3>
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-400">
            <span>{metrics.distinctCount} Farklı Kalem</span>
            <span className="font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
              {metrics.ticketCount} Serviste Kullanıldı
            </span>
          </div>
        </div>

        {/* Card 2: Toplam Parça Satış Tutarı */}
        <div className="bg-white rounded-2xl p-4.5 border border-slate-200/80 shadow-sm space-y-1.5">
          <div className="text-slate-500 text-xs font-semibold">
            <span>Müşteriye Yansıtılan Parça Satışı</span>
          </div>
          <h3 className="text-2xl font-black text-slate-900">{formatTL(metrics.totalSale)}</h3>
          <div className="flex items-center justify-between text-[11px] text-slate-400">
            <span>Faturalandırılan Tutar</span>
            <span className="font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
              Parça Cirosu
            </span>
          </div>
        </div>

        {/* Card 3: Toplam Parça Alış Maliyeti */}
        <div className="bg-white rounded-2xl p-4.5 border border-slate-200/80 shadow-sm space-y-1.5">
          <div className="text-slate-500 text-xs font-semibold">
            <span>Toplam Parça Alış Maliyeti</span>
          </div>
          <h3 className="text-2xl font-black text-rose-600">{formatTL(metrics.totalCost)}</h3>
          <div className="flex items-center justify-between text-[11px] text-slate-400">
            <span>Depo Çıkış / Toptan Maliyet</span>
            <span className="font-bold text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded">
              Alış Bedeli
            </span>
          </div>
        </div>

        {/* Card 4: Parça Net Kârı */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white rounded-2xl p-4.5 shadow-md space-y-1.5 relative overflow-hidden border border-slate-700">
          <div className="text-slate-300 text-xs font-semibold">
            <span>Parça Satışından Net Kâr</span>
          </div>
          <h3 className="text-2xl font-black text-emerald-400 tracking-tight">{formatTL(metrics.netProfit)}</h3>
          <div className="flex items-center justify-between text-[11px] text-slate-300 pt-0.5 border-t border-slate-700/60">
            <span>Kâr Marjı: <b className="text-emerald-300">%{metrics.marginPercent}</b></span>
            <span className="text-slate-400">Satış - Maliyet</span>
          </div>
        </div>
      </div>

      {/* Dolar ($) Alış & Kur Farkı Takip Analiz Kartı */}
      <div className="bg-gradient-to-r from-blue-950 via-slate-900 to-slate-900 text-white rounded-2xl p-4.5 border border-blue-800/40 shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-500/20 text-blue-400 rounded-xl border border-blue-500/30">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-sm text-white flex items-center gap-2">
                <span>Dolar ($) Bazlı Stok Alış, Satış & Kur Farkı Takibi</span>
                <span className="text-[10px] font-bold bg-blue-500/20 text-blue-300 border border-blue-400/30 px-2 py-0.5 rounded-full">
                  Canlı & Geçmiş Kur
                </span>
              </h4>
              <p className="text-xs text-slate-400">
                Stok alımındaki Dolar kuru ile satış anındaki kur farkı ve döviz bazlı kârlılık takibi
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-slate-800/80 px-3 py-1.5 rounded-xl border border-slate-700 font-mono text-xs">
            <span className="text-slate-400">Güncel TCMB Kuru:</span>
            <span className="font-bold text-emerald-400">1 USD = {metrics.currentRate.toFixed(4)} ₺</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
          {/* 1. Dolar Alış Hacmi */}
          <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/60 space-y-1">
            <div className="text-[11px] text-slate-400">Toplam Dolar Alışı ($)</div>
            <div className="text-lg font-black font-mono text-amber-400">
              {formatUSD(metrics.totalCostUSD)}
            </div>
            <div className="text-[10.5px] text-slate-400">
              Ort. Alış Kuru: <b className="text-slate-200">{metrics.avgCostRate ? `${metrics.avgCostRate.toFixed(2)} ₺` : '-'}</b>
            </div>
          </div>

          {/* 2. Dolar Satış Hacmi */}
          <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/60 space-y-1">
            <div className="text-[11px] text-slate-400">Toplam Dolar Satışı ($)</div>
            <div className="text-lg font-black font-mono text-emerald-400">
              {formatUSD(metrics.totalSaleUSD)}
            </div>
            <div className="text-[10.5px] text-slate-400">
              Ort. Satış Kuru: <b className="text-slate-200">{metrics.avgSaleRate ? `${metrics.avgSaleRate.toFixed(2)} ₺` : '-'}</b>
            </div>
          </div>

          {/* 3. Ortalama Kur Değişimi */}
          <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/60 space-y-1">
            <div className="text-[11px] text-slate-400">Ortalama Kur Değişimi</div>
            <div className={`text-lg font-black font-mono flex items-center gap-1 ${
              (metrics.rateDiff || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
            }`}>
              {(metrics.rateDiff || 0) >= 0 ? '+' : ''}{metrics.rateDiff !== undefined ? `${metrics.rateDiff.toFixed(2)} ₺` : '0.00 ₺'}
            </div>
            <div className="text-[10.5px] text-slate-400">
              USD/TL Satış - Alış Farkı
            </div>
          </div>

          {/* 4. Kur Farkından Doğan Kâr/Fark */}
          <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/60 space-y-1">
            <div className="text-[11px] text-slate-400">Kur Farkı TL Geliri</div>
            <div className={`text-lg font-black font-mono ${
              metrics.totalFxGainTL >= 0 ? 'text-emerald-400' : 'text-rose-400'
            }`}>
              {formatTL(metrics.totalFxGainTL)}
            </div>
            <div className="text-[10.5px] text-slate-400">
              Kur artışı kaynaklı TL farkı
            </div>
          </div>
        </div>
      </div>

      {/* Visual Analytics Grid: Top Used Parts Bar Chart & Category Pie Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Chart 1: En Çok Kullanılan / Düşen Parçalar */}
        <div className="lg:col-span-7 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <Boxes className="w-4 h-4 text-blue-600" />
                <span>En Çok Kullanılan Parçalar (Top 8)</span>
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Seçilen dönemde servislerde adet bazında en çok tüketilen yedek parçalar
              </p>
            </div>
            <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">
              {topUsedPartsChartData.length} Parça
            </span>
          </div>

          <div className="h-64 w-full">
            {topUsedPartsChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topUsedPartsChartData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#64748b' }} unit=" Ad." />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fontSize: 10, fill: '#334155', fontWeight: 600 }}
                    width={110}
                  />
                  <Tooltip
                    formatter={(val: any, name: string) => {
                      if (name === 'Adet') return [`${val} Adet`, 'Kullanım'];
                      return [formatTL(val), name];
                    }}
                    labelFormatter={(_, payload) => {
                      if (payload && payload[0]) {
                        return payload[0].payload.fullName;
                      }
                      return '';
                    }}
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      color: '#fff',
                      borderRadius: '10px',
                      fontSize: '11px',
                      border: 'none',
                    }}
                  />
                  <Bar dataKey="quantity" name="Adet" fill="#2563eb" radius={[0, 6, 6, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-400 text-xs">
                <Package className="w-8 h-8 text-slate-300 mb-2 stroke-[1.5]" />
                <span>Bu dönemde kullanılan parça verisi bulunmuyor</span>
              </div>
            )}
          </div>
        </div>

        {/* Chart 2: Kategori Dağılımı */}
        <div className="lg:col-span-5 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <Layers className="w-4 h-4 text-purple-600" />
                <span>Kategori Bazında Parça Tüketimi</span>
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Kullanılan parçaların ürün kategorilerine göre dağılımı
              </p>
            </div>
          </div>

          <div className="h-64 w-full flex items-center justify-center">
            {categoryPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {categoryPieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(val: any, name: string, item: any) => [
                      `${val} Adet (${formatTL(item.payload.sale)})`,
                      name,
                    ]}
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      color: '#fff',
                      borderRadius: '10px',
                      fontSize: '11px',
                      border: 'none',
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center text-slate-400 text-xs">
                <Layers className="w-8 h-8 text-slate-300 mb-2 stroke-[1.5]" />
                <span>Kategori verisi bulunmuyor</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Stock Deduction & Used Parts Data Table Section */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        {/* Table Header Controls */}
        <div className="p-4 sm:p-5 border-b border-slate-200/80 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-3 bg-slate-50/50">
          <div className="flex flex-wrap items-center gap-3">
            {/* View Tab Switcher (Detaylı vs Kümülatif) */}
            <div className="flex items-center p-1 bg-slate-200/80 rounded-xl">
              <button
                onClick={() => setViewTab('detailed')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  viewTab === 'detailed'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Detaylı Servis Dökümü ({filteredDeductions.length})
              </button>
              <button
                onClick={() => setViewTab('consolidated')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  viewTab === 'consolidated'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Parça Bazında Kümülatif ({consolidatedParts.length})
              </button>
            </div>

            {/* Category Filter */}
            {uniqueCategories.length > 0 && (
              <div className="flex items-center space-x-1.5 bg-white px-2.5 py-1.5 rounded-xl border border-slate-200 text-xs">
                <Filter className="w-3.5 h-3.5 text-slate-400" />
                <select
                  value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                  className="bg-transparent font-medium text-slate-700 focus:outline-none cursor-pointer"
                >
                  <option value="all">Tüm Kategoriler ({allDeductions.length})</option>
                  {uniqueCategories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Status Filter */}
            <div className="flex items-center space-x-1.5 bg-white px-2.5 py-1.5 rounded-xl border border-slate-200 text-xs">
              <PackageCheck className="w-3.5 h-3.5 text-slate-400" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as any)}
                className="bg-transparent font-medium text-slate-700 focus:outline-none cursor-pointer"
              >
                <option value="all">Tüm Stok Tipleri</option>
                <option value="stocked">Depo / Stoklu Parçalar</option>
                <option value="external">Harici Parçalar</option>
                <option value="critical">Kritik / Tükenen Depo Parçaları</option>
              </select>
            </div>

            {/* Currency Filter */}
            <div className="flex items-center space-x-1.5 bg-white px-2.5 py-1.5 rounded-xl border border-slate-200 text-xs">
              <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
              <select
                value={currencyFilter}
                onChange={e => setCurrencyFilter(e.target.value as any)}
                className="bg-transparent font-medium text-slate-700 focus:outline-none cursor-pointer"
              >
                <option value="all">Tüm Para Birimleri</option>
                <option value="USD">Dolar ($) Bazlı Parçalar</option>
                <option value="TRY">Türk Lirası (₺) Parçalar</option>
              </select>
            </div>
          </div>

          {/* Search Box & Quick Export */}
          <div className="flex items-center gap-2.5 w-full xl:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Parça adı, kod, servis no, müşteri..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={onExportPDF}
                title="Stok Raporunu PDF Olarak İndir"
                className="p-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-xs transition-transform hover:scale-105 cursor-pointer"
              >
                <FileText className="w-3.5 h-3.5 text-emerald-400" />
              </button>
              <button
                onClick={onExportExcel}
                title="Stok Raporunu Excel (.xlsx) Olarak İndir"
                className="p-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl shadow-xs transition-transform hover:scale-105 cursor-pointer"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-200" />
              </button>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          {viewTab === 'detailed' ? (
            /* 1. DETAYLI SERVİS PARÇA DÖKÜMÜ */
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-100/80 text-slate-600 font-bold border-b border-slate-200 select-none">
                <tr>
                  <th className="py-3 px-3">Tarih</th>
                  <th className="py-3 px-3">Servis No</th>
                  <th className="py-3 px-3">Müşteri & Cihaz</th>
                  <th className="py-3 px-3">Parça Adı & Kodu</th>
                  <th className="py-3 px-3">Kategori</th>
                  <th className="py-3 px-3 text-center">Kullanılan</th>
                  <th className="py-3 px-3 text-center">Depo Kalan</th>
                  <th className="py-3 px-3 text-right">Birim Alış & Alış Kuru</th>
                  <th className="py-3 px-3 text-right">Birim Satış & Satış Kuru</th>
                  <th className="py-3 px-3 text-right">Kur Farkı / Hesap</th>
                  <th className="py-3 px-3 text-right">Top. Maliyet</th>
                  <th className="py-3 px-3 text-right">Top. Satış</th>
                  <th className="py-3 px-3 text-right">Net Kâr</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {filteredDeductions.length > 0 ? (
                  filteredDeductions.map(item => (
                    <tr key={item.id} className="hover:bg-blue-50/40 transition-colors">
                      {/* Tarih */}
                      <td className="py-2.5 px-3 font-medium whitespace-nowrap text-slate-500">
                        {item.date || '-'}
                      </td>

                      {/* Servis No */}
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <span className="font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">
                          #{item.ticketId}
                        </span>
                      </td>

                      {/* Müşteri & Cihaz */}
                      <td className="py-2.5 px-3">
                        <div className="font-bold text-slate-900">{item.customerName || 'Belirtilmedi'}</div>
                        <div className="text-[11px] text-slate-500 truncate max-w-[160px]">{item.device || '-'}</div>
                        {item.customerPhone && (
                          <div className="text-[10px] font-mono text-slate-400">{formatPhoneDisplay(item.customerPhone)}</div>
                        )}
                      </td>

                      {/* Parça Adı & Kodu */}
                      <td className="py-2.5 px-3">
                        <div className="font-bold text-slate-900 flex items-center gap-1">
                          <span>{item.partName}</span>
                          {item.isUSD && (
                            <span className="text-[9.5px] font-bold bg-amber-100 text-amber-800 px-1 rounded border border-amber-200">
                              USD
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] font-mono text-slate-400 flex items-center gap-1">
                          <Barcode className="w-3 h-3 text-slate-400" />
                          <span>{item.partCode}</span>
                        </div>
                      </td>

                      {/* Kategori */}
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <span className="bg-slate-100 text-slate-700 font-medium px-2 py-0.5 rounded-full text-[10.5px]">
                          {item.category}
                        </span>
                      </td>

                      {/* Kullanılan Miktar */}
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        <span className="font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded">
                          {item.quantity} Adet
                        </span>
                      </td>

                      {/* Depo Kalan Durumu */}
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        {item.isStocked ? (
                          <span
                            className={`font-bold px-2 py-0.5 rounded text-[11px] ${
                              typeof item.currentStock === 'number' && item.currentStock <= 0
                                ? 'bg-rose-100 text-rose-800 border border-rose-200'
                                : item.isCritical
                                ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                            }`}
                          >
                            {item.currentStock} Adet
                          </span>
                        ) : (
                          <span className="text-[10.5px] text-slate-400 font-medium">Harici</span>
                        )}
                      </td>

                      {/* Birim Alış & Alış Kuru */}
                      <td className="py-2.5 px-3 text-right font-mono whitespace-nowrap">
                        <div className="text-slate-700 font-semibold">{formatTL(item.unitCost)}</div>
                        {item.isUSD && (
                          <div className="text-[10px] text-amber-700 font-medium flex items-center justify-end gap-1">
                            {item.costPriceUSD ? formatUSD(item.costPriceUSD) : ''}
                            {item.costExchangeRate && (
                              <span className="text-[9px] bg-amber-50 px-1 py-0.2 rounded border border-amber-200 text-amber-800">
                                1$={item.costExchangeRate.toFixed(2)}₺
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Birim Satış & Satış Kuru */}
                      <td className="py-2.5 px-3 text-right font-mono whitespace-nowrap">
                        <div className="text-slate-900 font-bold">{formatTL(item.unitPrice)}</div>
                        {item.isUSD && (
                          <div className="text-[10px] text-emerald-700 font-medium flex items-center justify-end gap-1">
                            {item.salePriceUSD ? formatUSD(item.salePriceUSD) : ''}
                            {item.saleExchangeRate && (
                              <span className="text-[9px] bg-emerald-50 px-1 py-0.2 rounded border border-emerald-200 text-emerald-800">
                                1$={item.saleExchangeRate.toFixed(2)}₺
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Kur Farkı / Hesap Bilgisi */}
                      <td className="py-2.5 px-3 text-right font-mono whitespace-nowrap">
                        {item.isUSD && item.rateDifference !== undefined ? (
                          <div>
                            <div className={`text-[11px] font-bold ${
                              item.rateDifference >= 0 ? 'text-emerald-700' : 'text-rose-700'
                            }`}>
                              {item.rateDifference >= 0 ? '+' : ''}{item.rateDifference.toFixed(2)} ₺/USD
                            </div>
                            {item.fxGainTL !== undefined && (
                              <div className="text-[10px] text-slate-500">
                                Kur Etkisi: <b className={item.fxGainTL >= 0 ? 'text-emerald-600' : 'text-rose-600'}>{formatTL(item.fxGainTL)}</b>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-[10.5px]">TL Sabit</span>
                        )}
                      </td>

                      {/* Top. Maliyet */}
                      <td className="py-2.5 px-3 text-right font-mono text-rose-700 font-medium whitespace-nowrap">
                        {formatTL(item.totalCost)}
                      </td>

                      {/* Top. Satış */}
                      <td className="py-2.5 px-3 text-right font-mono text-slate-900 font-bold whitespace-nowrap">
                        {formatTL(item.totalSale)}
                      </td>

                      {/* Net Kâr */}
                      <td className="py-2.5 px-3 text-right whitespace-nowrap">
                        <div className="font-mono font-bold text-emerald-700">
                          {formatTL(item.netProfit)}
                        </div>
                        <div className="text-[10px] text-slate-400">%{item.marginPercent} Marj</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={13} className="py-12 text-center text-slate-400">
                      <Package className="w-8 h-8 text-slate-300 mx-auto mb-2 stroke-[1.5]" />
                      <span>Seçilen filtrelere uygun kullanılan parça veya stok düşümü bulunamadı.</span>
                    </td>
                  </tr>
                )}
              </tbody>

              {filteredDeductions.length > 0 && (
                <tfoot>
                  <tr className="bg-slate-100 border-t-2 border-slate-300 text-xs font-bold text-slate-900">
                    <td colSpan={5} className="py-3 px-3 text-right">
                      Döküm Toplamları ({filteredDeductions.length} Kalem):
                    </td>
                    <td className="py-3 px-3 text-center font-mono">
                      {filteredDeductions.reduce((s, d) => s + d.quantity, 0)} Adet
                    </td>
                    <td className="py-3 px-3 text-center">-</td>
                    <td className="py-3 px-3 text-right font-mono text-slate-600">-</td>
                    <td className="py-3 px-3 text-right font-mono text-slate-600">-</td>
                    <td className="py-3 px-3 text-right font-mono text-emerald-700">
                      {formatTL(filteredDeductions.reduce((s, d) => s + (d.fxGainTL || 0), 0))}
                    </td>
                    <td className="py-3 px-3 text-right font-mono text-rose-700">
                      {formatTL(filteredDeductions.reduce((s, d) => s + d.totalCost, 0))}
                    </td>
                    <td className="py-3 px-3 text-right font-mono text-slate-900">
                      {formatTL(filteredDeductions.reduce((s, d) => s + d.totalSale, 0))}
                    </td>
                    <td className="py-3 px-3 text-right font-mono text-emerald-700">
                      {formatTL(filteredDeductions.reduce((s, d) => s + d.netProfit, 0))}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          ) : (
            /* 2. PARÇA BAZINDA KÜMÜLATİF ÖZET */
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-100/80 text-slate-600 font-bold border-b border-slate-200 select-none">
                <tr>
                  <th className="py-3 px-3">Parça / Ürün Adı</th>
                  <th className="py-3 px-3">Stok Kodu</th>
                  <th className="py-3 px-3">Kategori</th>
                  <th className="py-3 px-3 text-center">Toplam Kullanılan</th>
                  <th className="py-3 px-3 text-center">Servis Sayısı</th>
                  <th className="py-3 px-3 text-center">Depodaki Güncel Stok</th>
                  <th className="py-3 px-3 text-right">Ort. Alış & Alış Kuru</th>
                  <th className="py-3 px-3 text-right">Ort. Satış & Satış Kuru</th>
                  <th className="py-3 px-3 text-right">Kur Farkı / Hesap</th>
                  <th className="py-3 px-3 text-right">Toplam Alış (Maliyet)</th>
                  <th className="py-3 px-3 text-right">Toplam Satış Tutarı</th>
                  <th className="py-3 px-3 text-right">Toplam Net Kâr</th>
                  <th className="py-3 px-3 text-right">Kâr Marjı</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {consolidatedParts.length > 0 ? (
                  consolidatedParts.map((part, idx) => (
                    <tr key={part.key} className="hover:bg-blue-50/40 transition-colors">
                      {/* Parça Adı */}
                      <td className="py-2.5 px-3">
                        <div className="font-bold text-slate-900 flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 text-[10px] flex items-center justify-center font-mono">
                            {idx + 1}
                          </span>
                          <span>{part.partName}</span>
                          {part.isUSD && (
                            <span className="text-[9.5px] font-bold bg-amber-100 text-amber-800 px-1 rounded border border-amber-200">
                              USD
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Stok Kodu */}
                      <td className="py-2.5 px-3 whitespace-nowrap font-mono text-slate-500 font-bold">
                        {part.partCode}
                      </td>

                      {/* Kategori */}
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <span className="bg-slate-100 text-slate-700 font-medium px-2 py-0.5 rounded-full text-[10.5px]">
                          {part.category}
                        </span>
                      </td>

                      {/* Toplam Kullanılan Adet */}
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        <span className="font-bold text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded-full border border-blue-100">
                          {part.totalQuantity} Adet
                        </span>
                      </td>

                      {/* Servis Sayısı */}
                      <td className="py-2.5 px-3 text-center whitespace-nowrap text-slate-600 font-medium">
                        {part.timesUsed} Servis
                      </td>

                      {/* Depodaki Güncel Stok */}
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        {part.isStocked ? (
                          <span
                            className={`font-bold px-2 py-0.5 rounded text-[11px] ${
                              typeof part.currentStock === 'number' && part.currentStock <= 0
                                ? 'bg-rose-100 text-rose-800 border border-rose-200'
                                : part.isCritical
                                ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                            }`}
                          >
                            {part.currentStock} Adet
                          </span>
                        ) : (
                          <span className="text-[10.5px] text-slate-400">Harici</span>
                        )}
                      </td>

                      {/* Ort. Alış & Alış Kuru */}
                      <td className="py-2.5 px-3 text-right font-mono whitespace-nowrap">
                        <div className="text-slate-700 font-semibold">
                          {formatTL(part.totalQuantity > 0 ? part.totalCost / part.totalQuantity : 0)}
                        </div>
                        {part.isUSD && (
                          <div className="text-[10px] text-amber-700 font-medium">
                            {part.totalCostUSD ? formatUSD(part.totalCostUSD / part.totalQuantity) : ''}
                            {part.avgCostRate && (
                              <span className="text-[9px] bg-amber-50 px-1 py-0.2 rounded border border-amber-200 text-amber-800 ml-1">
                                1$={part.avgCostRate.toFixed(2)}₺
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Ort. Satış & Satış Kuru */}
                      <td className="py-2.5 px-3 text-right font-mono whitespace-nowrap">
                        <div className="text-slate-900 font-bold">
                          {formatTL(part.totalQuantity > 0 ? part.totalSale / part.totalQuantity : 0)}
                        </div>
                        {part.isUSD && (
                          <div className="text-[10px] text-emerald-700 font-medium">
                            {part.totalSaleUSD ? formatUSD(part.totalSaleUSD / part.totalQuantity) : ''}
                            {part.avgSaleRate && (
                              <span className="text-[9px] bg-emerald-50 px-1 py-0.2 rounded border border-emerald-200 text-emerald-800 ml-1">
                                1$={part.avgSaleRate.toFixed(2)}₺
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Kur Farkı & Hesap Bilgisi */}
                      <td className="py-2.5 px-3 text-right font-mono whitespace-nowrap">
                        {part.isUSD && part.rateDifference !== undefined ? (
                          <div>
                            <div className={`text-[11px] font-bold ${
                              part.rateDifference >= 0 ? 'text-emerald-700' : 'text-rose-700'
                            }`}>
                              {part.rateDifference >= 0 ? '+' : ''}{part.rateDifference.toFixed(2)} ₺/USD
                            </div>
                            {part.totalFxGainTL !== undefined && (
                              <div className="text-[10px] text-slate-500">
                                Toplam: <b className={part.totalFxGainTL >= 0 ? 'text-emerald-600' : 'text-rose-600'}>{formatTL(part.totalFxGainTL)}</b>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-[10.5px]">TL Sabit</span>
                        )}
                      </td>

                      {/* Toplam Alış Maliyeti */}
                      <td className="py-2.5 px-3 text-right font-mono text-rose-700 font-medium whitespace-nowrap">
                        {formatTL(part.totalCost)}
                      </td>

                      {/* Toplam Satış Tutarı */}
                      <td className="py-2.5 px-3 text-right font-mono text-slate-900 font-bold whitespace-nowrap">
                        {formatTL(part.totalSale)}
                      </td>

                      {/* Toplam Net Kâr */}
                      <td className="py-2.5 px-3 text-right font-mono text-emerald-700 font-bold whitespace-nowrap">
                        {formatTL(part.netProfit)}
                      </td>

                      {/* Kâr Marjı */}
                      <td className="py-2.5 px-3 text-right whitespace-nowrap">
                        <span className="font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-[11px]">
                          %{part.marginPercent}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={13} className="py-12 text-center text-slate-400">
                      <Package className="w-8 h-8 text-slate-300 mx-auto mb-2 stroke-[1.5]" />
                      <span>Kayıtlı parça kullanımı bulunamadı.</span>
                    </td>
                  </tr>
                )}
              </tbody>

              {consolidatedParts.length > 0 && (
                <tfoot>
                  <tr className="bg-slate-100 border-t-2 border-slate-300 text-xs font-bold text-slate-900">
                    <td colSpan={3} className="py-3 px-3 text-right">
                      Konsolide Toplam ({consolidatedParts.length} Çeşit):
                    </td>
                    <td className="py-3 px-3 text-center font-mono text-blue-700">
                      {consolidatedParts.reduce((s, p) => s + p.totalQuantity, 0)} Adet
                    </td>
                    <td className="py-3 px-3 text-center">-</td>
                    <td className="py-3 px-3 text-center">-</td>
                    <td className="py-3 px-3 text-right font-mono text-slate-600">-</td>
                    <td className="py-3 px-3 text-right font-mono text-slate-600">-</td>
                    <td className="py-3 px-3 text-right font-mono text-emerald-700">
                      {formatTL(consolidatedParts.reduce((s, p) => s + (p.totalFxGainTL || 0), 0))}
                    </td>
                    <td className="py-3 px-3 text-right font-mono text-rose-700">
                      {formatTL(consolidatedParts.reduce((s, p) => s + p.totalCost, 0))}
                    </td>
                    <td className="py-3 px-3 text-right font-mono text-slate-900">
                      {formatTL(consolidatedParts.reduce((s, p) => s + p.totalSale, 0))}
                    </td>
                    <td className="py-3 px-3 text-right font-mono text-emerald-700">
                      {formatTL(consolidatedParts.reduce((s, p) => s + p.netProfit, 0))}
                    </td>
                    <td className="py-3 px-3 text-right font-mono">
                      %{metrics.marginPercent}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
